import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { MessageTemplatesPanel } from '../components/MessageTemplatesPanel';
import { CampaignTable } from '../components/CampaignTable';
import { buildApiUrl } from '../services/api';

type MessageContent =
  | { text: string }
  | { url: string; caption?: string }
  | { url: string; fileName?: string }
  | { sequence: Array<{ type: string; content: any }> };

interface Campaign {
  id: string;
  nome: string;
  missionId?: string | null;
  channels?: Array<'WHATSAPP' | 'EMAIL'>;
  channelConfig?: {
    whatsapp?: {
      sessionNames: string[];
      messageType: string;
      messageContent: MessageContent;
    };
    email?: {
      subject: string;
      body: string;
    };
  };
  targetTags: string[];
  sessionNames: string[];
  sessionName: string;
  messageType: string;
  messageContent: MessageContent;
  randomDelay: number;
  startImmediately: boolean;
  scheduledFor: string | null;
  status: string;
  totalContacts: number;
  sentCount: number;
  failedCount: number;
  startedAt: string | null;
  completedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  criadoEm: string;
  session: {
    name: string;
    displayName?: string;
    status: string;
    mePushName: string | null;
  };
  _count: {
    messages: number;
  };
}

interface WhatsAppSession {
  name: string; // Nome real usado na API
  displayName?: string; // Nome exibido ao usuário
  mePushName: string | null;
  meId: string | null;
}

interface ContactTag {
  id: string;
  nome: string;
}

interface SavedMessageTemplate {
  id: string;
  name: string;
  content: string;
  active: boolean;
  description?: string | null;
}

interface EmailMediaItem {
  type: 'image' | 'video';
  url: string;
  fileName: string;
  mimeType?: string;
}

interface ChannelUsageSummary {
  usedToday: number;
  usedMonth: number;
  dailyLimit: number | null;
  monthlyLimit: number | null;
}

interface MessageUsageSummary {
  planId: string;
  planTitle: string;
  channels: {
    WHATSAPP: ChannelUsageSummary;
    EMAIL: ChannelUsageSummary;
  };
  reset: {
    lastDailyResetAt: string;
    monthlyCompetence: string;
    timezone: string;
  };
}

const normalizeSavedTemplateName = (value: string) => value.trim().toLocaleLowerCase('pt-BR');

const convertPlainTextToHtml = (value: string) => {
  const normalized = value.trim();

  if (!normalized) return '';

  return normalized
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
    .join('');
};

const stripHtml = (value: string) => value
  .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
  .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/<\/p>/gi, '\n')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeEmailEditorHtml = (value: string) => {
  const normalized = value.trim();

  if (!normalized) return '';

  if (/<[a-z][\s\S]*>/i.test(normalized)) {
    return normalized;
  }

  return convertPlainTextToHtml(normalized);
};

const hasMeaningfulEmailContent = (value: string) => {
  const normalized = value.trim();

  if (!normalized) return false;

  return stripHtml(normalized).length > 0 || /<(img|video)\b/i.test(normalized);
};

const isEditorEffectivelyEmpty = (value: string) => {
  const normalized = value
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<p>\s*<\/p>/gi, '')
    .trim();

  return !normalized || stripHtml(normalized).length === 0;
};

const formatMetricNumber = (value: number | null) => {
  if (value === null) return 'Ilimitado';
  return new Intl.NumberFormat('pt-BR').format(value);
};

const formatUsageText = (used: number, limit: number | null, singularLabel: string, pluralLabel: string) => {
  const noun = limit === 1 ? singularLabel : pluralLabel;
  if (limit === null) {
    return `${formatMetricNumber(used)} ${noun} utilizadas`;
  }
  return `${formatMetricNumber(used)} de ${formatMetricNumber(limit)} ${noun} utilizadas`;
};

const PLATFORM_SAVED_TEMPLATES: SavedMessageTemplate[] = [
  {
    id: 'platform-apresentacao-inicial',
    name: 'Apresentação Inicial',
    description: 'Mensagem usada para primeiro contato com o lead.',
    active: true,
    content: `Olá, {{nome}}! Tudo bem?

Meu nome é [SEU NOME] e sou Corretor de Consórcio/Seguro.
Estou entrando em contato para me apresentar e também me colocar à disposição para ajudar você a encontrar as melhores opções de proteção, segurança financeira e planejamento para seus objetivos.

Hoje existem oportunidades muito interessantes em seguros e consórcios que podem fazer sentido para o seu momento.

Se quiser, posso te enviar algumas informações sem compromisso 😊`
  },
  {
    id: 'platform-envio-promocao',
    name: 'Envio de Promoção',
    description: 'Mensagem promocional para campanhas.',
    active: true,
    content: `Olá, {{nome}}!

Passando para avisar que estamos com condições especiais em planos de Seguro/Consórcio por tempo limitado.

Como Corretor de Consórcio/Seguro, selecionei algumas opções com excelente custo-benefício que podem ajudar você a economizar e ainda garantir mais tranquilidade e segurança.

Se tiver interesse, posso te enviar uma simulação personalizada sem compromisso.`
  },
  {
    id: 'platform-novas-oportunidades',
    name: 'Novas Oportunidades',
    description: 'Aviso de novas ações e campanhas.',
    active: true,
    content: `Olá, {{nome}}! Tudo bem?

Quero te avisar que surgiram novas oportunidades e condições especiais em Seguro/Consórcio esta semana.

Como Corretor de Consórcio/Seguro, estou entrando em contato com alguns clientes para apresentar essas novidades antes do encerramento das campanhas.

Se quiser receber mais detalhes ou uma simulação atualizada, é só me chamar 😊`
  }
];

type CampaignChannel = 'WHATSAPP' | 'EMAIL';

const initialFormData = {
  nome: '',
  channels: [] as CampaignChannel[],
  targetTags: [] as string[],
  sessionNames: [] as string[],
  sessionName: '',
  messageType: 'sequence',
  messageContent: { sequence: [] as Array<{ type: string; content: any }> } as MessageContent,
  emailSubject: '',
  emailBody: '',
  emailMedia: [] as EmailMediaItem[],
  randomDelay: 30,
  startImmediately: true,
  scheduledFor: ''
};

export function CampaignsPage() {
  const [searchParams] = useSearchParams();
  const missionId = searchParams.get('missionId');
  const emailBodyEditorRef = useRef<HTMLDivElement | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStep, setCreateStep] = useState<'channel' | 'form'>('channel');
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [currentReportCampaignId, setCurrentReportCampaignId] = useState<string | null>(null);
  const [reportCurrentPage, setReportCurrentPage] = useState(1);
  const [reportItemsPerPage] = useState(8);
  const [campaignsCurrentPage, setCampaignsCurrentPage] = useState(1);
  const [campaignsPerPage] = useState(10);
  const [contactTags, setContactTags] = useState<ContactTag[]>([]);
  const [whatsappSessions, setWhatsappSessions] = useState<WhatsAppSession[]>([]);
  const [savedTemplates, setSavedTemplates] = useState<SavedMessageTemplate[]>([]);
  const [selectedSavedTemplateId, setSelectedSavedTemplateId] = useState('');
  const [activeSavedTemplateMessageIndex, setActiveSavedTemplateMessageIndex] = useState<number | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({});
  const [fileInfos, setFileInfos] = useState<Record<string, { name: string, size: number, type: string }>>({});
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [campaignsPageTab, setCampaignsPageTab] = useState<'actions' | 'templates' | 'usage'>('actions');
  const [usageSummary, setUsageSummary] = useState<MessageUsageSummary | null>(null);

  const reportTotals = reportData?.stats || {};
  const reportTotal = Number(reportTotals.total ?? 0);
  const reportSent = Number(reportTotals.sent ?? 0);
  const reportFailed = Number(reportTotals.failed ?? 0);
  const reportPending = Number(
    reportTotals.pending ?? Math.max(reportTotal - reportSent - reportFailed, 0)
  );
  const reportProgress = reportTotal > 0
    ? Math.min(100, Math.round(((reportSent + reportFailed) / reportTotal) * 100))
    : 0;
  const reportPendingMessages = (reportData?.campaign?.messages || []).filter(
    (message: any) => message.status !== 'SENT' && message.status !== 'FAILED'
  );
  const reportInvalidMessages = (reportData?.campaign?.messages || []).filter((message: any) => {
    const errorMessage = String(message?.errorMessage || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    if (!message?.contactId || message?.status !== 'FAILED') {
      return false;
    }

    return [
      'numero nao existe',
      'numero nao encontrado',
      'numero invalido',
      'telefone invalido',
      '"exists":false',
      '"exists": false',
      '"jid":"',
      '"jid": "',
      'whatsapp invalid',
      'number invalid',
      'invalid number',
      'invalid phone',
      'email invalido',
      'e-mail invalido',
      'invalid email',
      'destinatario invalido',
      'recipient invalid'
    ].some((pattern) => errorMessage.includes(pattern));
  });
  const reportInvalidContactsCount = new Set(
    reportInvalidMessages.map((message: any) => message.contactId).filter(Boolean)
  ).size;
  const completedCampaigns = campaigns.filter((campaign) => campaign.status === 'COMPLETED').length;
  const pausedCampaigns = campaigns.filter((campaign) => campaign.status === 'PAUSED').length;
  const totalContacts = campaigns.reduce((sum, campaign) => sum + Number(campaign.totalContacts || 0), 0);
  const totalSent = campaigns.reduce((sum, campaign) => sum + Number(campaign.sentCount || 0), 0);
  const selectedSavedTemplate = savedTemplates.find((template) => template.id === selectedSavedTemplateId) || null;
  const whatsappUsage = usageSummary?.channels.WHATSAPP || null;
  const emailUsage = usageSummary?.channels.EMAIL || null;

  // Form states
  const [formData, setFormData] = useState(initialFormData);
  const hasSelectedSession = formData.sessionNames.length > 0;
  const hasWhatsappChannel = formData.channels.includes('WHATSAPP');
  const hasEmailChannel = formData.channels.includes('EMAIL');

  useEffect(() => {
    loadCampaigns();
    loadContactTags();
    loadWhatsAppSessions();
    loadSavedTemplates();
    loadUsageSummary();
  }, [missionId]);

  useEffect(() => {
    if (!showCreateModal) return;
    if (!hasWhatsappChannel) return;
    if (hasSelectedSession) return;
    if (whatsappSessions.length === 0) return;

    setFormData(prev => ({
      ...prev,
      sessionNames: [whatsappSessions[0].name]
    }));
  }, [showCreateModal, hasSelectedSession, hasWhatsappChannel, whatsappSessions]);

  useEffect(() => {
    const editor = emailBodyEditorRef.current;
    if (!editor) return;

    const normalized = normalizeEmailEditorHtml(formData.emailBody);
    if (editor.innerHTML !== normalized) {
      editor.innerHTML = normalized;
    }
  }, [formData.emailBody, showCreateModal]);

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(buildApiUrl(url), {
      ...options,
      headers,
    });
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setPageError(null);
      const endpoint = missionId
        ? buildApiUrl(`/campaigns?missionId=${encodeURIComponent(missionId)}`)
        : buildApiUrl('/campaigns');
      const response = await authenticatedFetch(endpoint);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      const list = data.campaigns || [];
      setCampaigns(list);
      return list;
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      setPageError('Não foi possível carregar as ações de envio agora.');
      toast.error('Erro ao carregar campanhas');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const loadContactTags = async () => {
    try {
      const response = await authenticatedFetch(buildApiUrl('/campaigns/tags'));
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const tags = await response.json();
      setContactTags(tags);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  };

  const loadWhatsAppSessions = async () => {
    try {
      const response = await authenticatedFetch(buildApiUrl('/campaigns/sessions'));
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const sessions = await response.json();
      setWhatsappSessions(sessions);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    }
  };

  const loadSavedTemplates = async () => {
    try {
      const params = new URLSearchParams({
        mine: 'true',
        messageType: 'TEXT',
        active: 'true'
      });
      const response = await authenticatedFetch(buildApiUrl(`/templates?${params.toString()}`));
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const payload = await response.json();
      const loadedTemplates = (payload.data || []).filter((template: SavedMessageTemplate) => template.active);
      const templateMap = new Map(
        loadedTemplates.map((template: SavedMessageTemplate) => [
          normalizeSavedTemplateName(template.name),
          template
        ])
      );
      const templates = [
        ...PLATFORM_SAVED_TEMPLATES.map((template) =>
          templateMap.get(normalizeSavedTemplateName(template.name)) || template
        ),
        ...loadedTemplates.filter(
          (template: SavedMessageTemplate) =>
            !PLATFORM_SAVED_TEMPLATES.some(
              (platformTemplate) =>
                normalizeSavedTemplateName(platformTemplate.name) === normalizeSavedTemplateName(template.name)
            )
        )
      ];
      setSavedTemplates(templates);
      setSelectedSavedTemplateId((currentId) =>
        currentId && templates.some((template: SavedMessageTemplate) => template.id === currentId)
          ? currentId
          : (templates[0]?.id || '')
      );
    } catch (error) {
      console.error('Erro ao carregar templates salvos:', error);
      setSavedTemplates(PLATFORM_SAVED_TEMPLATES);
      setSelectedSavedTemplateId(PLATFORM_SAVED_TEMPLATES[0]?.id || '');
    }
  };

  const appendTextMessageToSequence = (text: string) => {
    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const newSequence = [...currentSequence, { type: 'text', content: { text } }];
    setFormData(prev => ({
      ...prev,
      messageContent: { sequence: newSequence }
    }));
    setActiveSavedTemplateMessageIndex(newSequence.length - 1);
  };

  const handleTagToggle = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      targetTags: prev.targetTags.includes(tag)
        ? prev.targetTags.filter(t => t !== tag)
        : [...prev.targetTags, tag]
    }));
  };

  const handleSessionToggle = (sessionName: string) => {
    setFormData(prev => ({
      ...prev,
      sessionNames: prev.sessionNames.includes(sessionName)
        ? prev.sessionNames.filter(s => s !== sessionName)
        : [...prev.sessionNames, sessionName]
    }));
  };

  const openCreateModal = () => {
    resetForm();
    setCreateStep('channel');
    setShowCreateModal(true);
  };

  const handleChannelSelection = (channels: CampaignChannel[]) => {
    setFormData(prev => ({
      ...prev,
      channels,
      sessionNames: channels.includes('WHATSAPP')
        ? (prev.sessionNames.length > 0
          ? prev.sessionNames
          : (whatsappSessions[0] ? [whatsappSessions[0].name] : []))
        : []
    }));
    setCreateStep('form');
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();

    if (creatingCampaign) return;

    if (formData.targetTags.length === 0) {
      toast.error('Selecione pelo menos uma categoria de contatos');
      return;
    }

    if (formData.channels.length === 0) {
      toast.error('Selecione pelo menos um canal de envio');
      return;
    }

    if (hasWhatsappChannel && formData.sessionNames.length === 0) {
      toast.error('Selecione pelo menos uma conexão WhatsApp');
      return;
    }

    if (hasWhatsappChannel && (!('sequence' in formData.messageContent) || formData.messageContent.sequence.length === 0)) {
      toast.error('Adicione pelo menos uma mensagem à campanha');
      return;
    }

    if (hasEmailChannel && !formData.emailSubject.trim()) {
      toast.error('Informe o assunto do email');
      return;
    }

    if (hasEmailChannel && !hasMeaningfulEmailContent(formData.emailBody)) {
      toast.error('Informe o conteúdo do email');
      return;
    }

    try {
      setCreatingCampaign(true);
      const campaignName = formData.nome;
      const sequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
      const isSequence = sequence.length > 1;

      let finalMessageType = 'text';
      let finalMessageContent: any = { text: normalizeEmailEditorHtml(formData.emailBody) };

      if (hasWhatsappChannel) {
        if (isSequence) {
          finalMessageType = 'sequence';
          finalMessageContent = { sequence };
        } else if (sequence.length === 1) {
          const singleMessage = sequence[0];
          finalMessageType = singleMessage.type;
          finalMessageContent = singleMessage.content;
        }
      }

      const campaignData = {
        ...formData,
        missionId,
        channels: formData.channels,
        messageType: finalMessageType,
        messageContent: finalMessageContent,
        channelConfig: {
          ...(hasWhatsappChannel ? {
            whatsapp: {
              sessionNames: formData.sessionNames,
              messageType: finalMessageType,
              messageContent: finalMessageContent
            }
          } : {}),
          ...(hasEmailChannel ? {
            email: {
              subject: formData.emailSubject,
              body: normalizeEmailEditorHtml(formData.emailBody),
              media: formData.emailMedia
            }
          } : {})
        },
        scheduledFor: formData.startImmediately ? null : formData.scheduledFor || null
      };

      const response = await authenticatedFetch(buildApiUrl('/campaigns'), {
        method: 'POST',
        body: JSON.stringify(campaignData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.errors && errorData.errors.length > 0) {
          const validationErrors = errorData.errors.map((err: any) => err.msg).join(', ');
          throw new Error(validationErrors);
        } else if (errorData.error) {
          throw new Error(errorData.error);
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      }

      let createdCampaignId: string | null = null;
      try {
        const responseData = await response.json();
        const createdCampaign = responseData?.campaign ?? responseData;
        createdCampaignId = createdCampaign?.id ?? null;
      } catch (parseError) {
        createdCampaignId = null;
      }

      toast.success('Campanha criada com sucesso!');
      setShowCreateModal(false);
      resetForm();
      loadUsageSummary();
      const campaignsList = await loadCampaigns();

      if (createdCampaignId) {
        handleViewReport(createdCampaignId);
      } else if (campaignsList.length > 0) {
        const candidates = campaignsList.filter((campaign) => campaign.nome === campaignName);
        const sorted = (candidates.length ? candidates : campaignsList).sort(
          (a, b) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime()
        );
        handleViewReport(sorted[0].id);
      }
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao criar campanha');
    } finally {
      setCreatingCampaign(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setUploadingFiles({});
    setFileInfos({});
    setSelectedSavedTemplateId(savedTemplates[0]?.id || '');
    setActiveSavedTemplateMessageIndex(null);
  };

  const handleUseSavedTemplate = (content: string) => {
    resetForm();
    setFormData({
      ...initialFormData,
      channels: ['WHATSAPP'],
      sessionNames: whatsappSessions[0] ? [whatsappSessions[0].name] : [],
      messageType: 'sequence',
      messageContent: { sequence: [{ type: 'text', content: { text: content } }] }
    });
    setCreateStep('form');
    setShowCreateModal(true);
    toast.success('Template carregado para nova campanha');
  };

  const handleUseSavedTemplateInSequence = (messageIndex: number) => {
    const selectedTemplate = savedTemplates.find((template) => template.id === selectedSavedTemplateId);

    if (!selectedTemplate) {
      toast.error('Selecione uma mensagem salva');
      return;
    }

    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const newSequence = currentSequence.map((seqItem, index) =>
      index === messageIndex
        ? {
            ...seqItem,
            content: {
              ...seqItem.content,
              useVariations: false,
              variations: undefined,
              text: selectedTemplate.content
            }
          }
        : seqItem
    );

    setFormData(prev => ({
      ...prev,
      messageContent: { sequence: newSequence }
    }));
    setActiveSavedTemplateMessageIndex(null);
    toast.success('Mensagem salva aplicada ao texto');
  };

  const handleUseSavedTemplateForEmail = () => {
    const selectedTemplate = savedTemplates.find((template) => template.id === selectedSavedTemplateId);

    if (!selectedTemplate) {
      toast.error('Selecione uma mensagem salva');
      return;
    }

    setFormData(prev => ({
      ...prev,
      emailBody: convertPlainTextToHtml(selectedTemplate.content)
    }));
    toast.success('Mensagem salva aplicada ao conteúdo do email');
  };

  const handleEmailBodyInput = () => {
    const html = emailBodyEditorRef.current?.innerHTML || '';
    setFormData(prev => ({
      ...prev,
      emailBody: isEditorEffectivelyEmpty(html) ? '' : html
    }));
  };

  const syncEmailBodyFromEditor = () => {
    const editor = emailBodyEditorRef.current;
    if (!editor) return;

    const html = editor.innerHTML || '';
    setFormData(prev => ({
      ...prev,
      emailBody: isEditorEffectivelyEmpty(html) ? '' : html
    }));
  };

  const insertHtmlIntoEmailEditor = (html: string) => {
    const editor = emailBodyEditorRef.current;
    if (!editor) return;

    editor.focus();

    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const selectionInsideEditor = !!range && editor.contains(range.commonAncestorContainer);

    if (selectionInsideEditor && range) {
      range.deleteContents();
      const fragment = range.createContextualFragment(html);
      const lastNode = fragment.lastChild;
      range.insertNode(fragment);

      if (lastNode) {
        const nextRange = document.createRange();
        nextRange.setStartAfter(lastNode);
        nextRange.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(nextRange);
      }
    } else {
      editor.insertAdjacentHTML('beforeend', html);
      const nextRange = document.createRange();
      nextRange.selectNodeContents(editor);
      nextRange.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(nextRange);
    }

    syncEmailBodyFromEditor();
  };

  const handleApplyEmailFormatting = (command: 'bold' | 'italic' | 'insertUnorderedList') => {
    document.execCommand(command, false);
    emailBodyEditorRef.current?.focus();
    handleEmailBodyInput();
  };

  const handleInlineEmailImageUpload = async (file: File) => {
    const uploadKey = `email-inline-image-${Date.now()}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(buildApiUrl('/media/upload'), {
        method: 'POST',
        body: uploadFormData,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload da imagem');
      }

      const data = await response.json();
      const imageHtml = `<p><img src="${data.fileUrl}" alt="${data.originalName}" style="max-width: 100%; height: auto; border-radius: 12px;" /></p>`;
      insertHtmlIntoEmailEditor(imageHtml);

      toast.success('Imagem adicionada ao conteÃºdo do email');
    } catch (error) {
      console.error('Erro ao inserir imagem no conteÃºdo do email:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao inserir imagem no conteÃºdo do email');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const loadUsageSummary = useCallback(async () => {
    try {
      const response = await authenticatedFetch(buildApiUrl('/campaigns/usage-summary'));
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      setUsageSummary(payload.data || null);
    } catch (error) {
      console.error('Erro ao carregar consumo de mensagens:', error);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadUsageSummary();
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [loadUsageSummary]);

  const handleFileUpload = async (file: File, messageIndex: number, variationIndex?: number) => {
    const uploadKey = variationIndex !== undefined ? `${messageIndex}-${variationIndex}` : messageIndex.toString();
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(buildApiUrl('/media/upload'), {
        method: 'POST',
        body: uploadFormData,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
      }

      const data = await response.json();

      // Armazenar informações do arquivo
      if (variationIndex !== undefined) {
        setFileInfos(prev => ({
          ...prev,
          [`${messageIndex}-${variationIndex}`]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      } else {
        setFileInfos(prev => ({
          ...prev,
          [messageIndex]: {
            name: data.originalName,
            size: data.size,
            type: data.mimetype
          }
        }));
      }

      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];

      if (variationIndex !== undefined) {
        // Upload para variação
        const newSequence = currentSequence.map((seqItem, i) => {
          if (i === messageIndex) {
            const mediaVariations = [...(seqItem.content.mediaVariations || [])];
            // Garantir que o array tenha o tamanho necessário
            while (mediaVariations.length <= variationIndex) {
              mediaVariations.push({ url: '', caption: '', fileName: '' });
            }
            mediaVariations[variationIndex] = {
              ...mediaVariations[variationIndex],
              url: data.fileUrl,
              fileName: data.originalName
            };
            return {
              ...seqItem,
              content: { ...seqItem.content, mediaVariations }
            };
          }
          return seqItem;
        });

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));
      } else {
        // Upload para modo single
        const newSequence = currentSequence.map((seqItem, i) =>
          i === messageIndex ? {
            ...seqItem,
            content: { ...seqItem.content, url: data.fileUrl }
          } : seqItem
        );

        setFormData(prev => ({
          ...prev,
          messageContent: { sequence: newSequence }
        }));
      }

      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleEmailMediaUpload = async (file: File, type: 'image' | 'video') => {
    const uploadKey = `email-${type}-${Date.now()}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(buildApiUrl('/media/upload'), {
        method: 'POST',
        body: uploadFormData,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
      }

      const data = await response.json();

      setFileInfos(prev => ({
        ...prev,
        [uploadKey]: {
          name: data.originalName,
          size: data.size,
          type: data.mimetype
        }
      }));

      setFormData(prev => ({
        ...prev,
        emailMedia: [
          ...prev.emailMedia,
          {
            type,
            url: data.fileUrl,
            fileName: data.originalName,
            mimeType: data.mimetype
          }
        ]
      }));

      toast.success(`${type === 'image' ? 'Imagem' : 'Vídeo'} adicionado ao email`);
    } catch (error) {
      console.error('Erro ao enviar mídia do email:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao enviar mídia do email');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleRemoveEmailMedia = (mediaIndex: number) => {
    setFormData(prev => ({
      ...prev,
      emailMedia: prev.emailMedia.filter((_, index) => index !== mediaIndex)
    }));
  };

  const handleRemoveFile = (messageIndex: number) => {
    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
    const newSequence = currentSequence.map((seqItem, i) =>
      i === messageIndex ? {
        ...seqItem,
        content: { ...seqItem.content, url: '' }
      } : seqItem
    );

    setFormData(prev => ({
      ...prev,
      messageContent: { sequence: newSequence }
    }));

    setFileInfos(prev => {
      const updated = { ...prev };
      delete updated[messageIndex];
      return updated;
    });

    toast.success('Arquivo removido');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    return '📄';
  };

  const handleToggleCampaign = async (campaignId: string, action: 'pause' | 'resume') => {
    try {
      const response = await authenticatedFetch(buildApiUrl(`/campaigns/${campaignId}/toggle`), {
        method: 'PATCH',
        body: JSON.stringify({ action })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      toast.success(`Campanha ${action === 'pause' ? 'pausada' : 'retomada'} com sucesso!`);
      loadCampaigns();
      loadUsageSummary();
    } catch (error) {
      console.error('Erro ao alterar status da campanha:', error);
      toast.error('Erro ao alterar status da campanha');
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta campanha?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(buildApiUrl(`/campaigns/${campaignId}`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      toast.success('Campanha excluída com sucesso!');
      loadCampaigns();
    } catch (error) {
      console.error('Erro ao excluir campanha:', error);
      toast.error('Erro ao excluir campanha');
    }
  };

  const handleViewReport = async (campaignId: string) => {
    setCurrentReportCampaignId(campaignId);
    setReportCurrentPage(1);
    setReportData(null);
    setReportLoading(true);
    setShowReportModal(true);

    try {
      const response = await authenticatedFetch(buildApiUrl(`/campaigns/${campaignId}/report`));

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar relatório');
        setShowReportModal(false);
      }
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      toast.error('Erro ao carregar relatório');
      setShowReportModal(false);
    } finally {
      setReportLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-gray-100 text-gray-800';
      case 'RUNNING':
        return 'bg-blue-100 text-blue-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'PAUSED':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Aguardando';
      case 'RUNNING':
        return 'Executando';
      case 'COMPLETED':
        return 'Concluída';
      case 'PAUSED':
        return 'Pausada';
      case 'FAILED':
        return 'Falha';
      default:
        return status;
    }
  };

  const handleRefreshReport = useCallback(async () => {
    if (!currentReportCampaignId) return;

    setReportLoading(true);
    try {
      const response = await authenticatedFetch(buildApiUrl(`/campaigns/${currentReportCampaignId}/report`));

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar relatório');
      }
    } catch (error) {
      console.error('Erro ao buscar relatório:', error);
      toast.error('Erro ao carregar relatório');
    } finally {
      setReportLoading(false);
    }
  }, [authenticatedFetch, currentReportCampaignId]);

  useEffect(() => {
    if (!showReportModal || !currentReportCampaignId) {
      return;
    }

    const status = reportData?.campaign?.status;
    const shouldPoll = !status || status === 'RUNNING' || status === 'PENDING';

    if (!shouldPoll) {
      return;
    }

    const interval = window.setInterval(() => {
      if (!reportLoading) {
        handleRefreshReport();
      }
    }, 5000);

    return () => {
      window.clearInterval(interval);
    };
  }, [showReportModal, currentReportCampaignId, reportData?.campaign?.status, reportLoading, handleRefreshReport]);

  const handleDownloadReport = async () => {
    if (!currentReportCampaignId || !reportData) return;

    try {
      const response = await authenticatedFetch(buildApiUrl(`/campaigns/${currentReportCampaignId}/report/download`));

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        const safeName = reportData.campaign.nome.replace(/[^a-zA-Z0-9]/g, '_');
        a.download = `relatorio_campanha_${safeName}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Relatório baixado com sucesso!');
      } else {
        if (response.status === 404) {
          const campaign = reportData.campaign;
          const stats = reportData.stats || {};
          const messages = campaign.messages || [];

          const csvRows: string[][] = [
            ['Relatório da Campanha'],
            ['Nome', campaign.nome],
            ['Status', getStatusText(campaign.status)],
            ['Criado por', campaign.createdByName || 'N/A'],
            ['Data de Criação', new Date(campaign.criadoEm).toLocaleString('pt-BR')],
            ['Data de Início', campaign.startedAt ? new Date(campaign.startedAt).toLocaleString('pt-BR') : 'N/A'],
            ['Data de Conclusão', campaign.completedAt ? new Date(campaign.completedAt).toLocaleString('pt-BR') : 'N/A'],
            [''],
            ['Estatísticas'],
            ['Total de Contatos', stats.total ?? 'N/A'],
            ['Mensagens Enviadas', stats.sent ?? 'N/A'],
            ['Mensagens Falharam', stats.failed ?? 'N/A'],
            ['Mensagens Pendentes', stats.pending ?? 'N/A'],
            [''],
            ['Detalhes das Mensagens'],
            ['Nome', 'Telefone', 'Status', 'Sessão Utilizada', 'Data de Envio', 'Erro']
          ];

          messages.forEach((message: any) => {
            csvRows.push([
              message.contactName,
              message.contactPhone,
              message.status,
              message.sessionName || 'N/A',
              message.sentAt ? new Date(message.sentAt).toLocaleString('pt-BR') : 'N/A',
              message.errorMessage || 'N/A'
            ]);
          });

          const csvContent = csvRows.map((row) =>
            row.map((field) => `"${String(field ?? '').replace(/"/g, '""')}"`).join(',')
          ).join('\n');

          const fallbackBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const fallbackUrl = window.URL.createObjectURL(fallbackBlob);
          const link = document.createElement('a');
          link.style.display = 'none';
          link.href = fallbackUrl;
          const safeName = campaign.nome.replace(/[^a-zA-Z0-9]/g, '_');
          link.download = `relatorio_campanha_${safeName}_${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(fallbackUrl);
          document.body.removeChild(link);
          toast.success('Relatório baixado localmente (endpoint indisponível).');
          return;
        }

        let errorMessage = 'Erro ao baixar relatório';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          const text = await response.text().catch(() => '');
          if (text) {
            errorMessage = text;
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Erro ao baixar relatório:', error);
      toast.error('Erro ao baixar relatório');
    }
  };

  const handleDeleteInvalidReportContacts = async () => {
    if (!currentReportCampaignId || !reportInvalidContactsCount) return;

    const confirmed = window.confirm(
      `Isso vai excluir ${reportInvalidContactsCount} contato(s) do arquivo do usuário porque tiveram número ou e-mail inválido neste relatório. Deseja continuar?`
    );

    if (!confirmed) return;

    try {
      const response = await authenticatedFetch(
        buildApiUrl(`/campaigns/${currentReportCampaignId}/report/delete-invalid-contacts`),
        { method: 'POST' }
      );

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || 'Erro ao excluir contatos inválidos');
      }

      toast.success(payload?.message || 'Contatos inválidos excluídos com sucesso!');
      await handleRefreshReport();
    } catch (error) {
      console.error('Erro ao excluir contatos inválidos:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao excluir contatos inválidos');
    }
  };

  const getErrorIcon = (errorMessage: string) => {
    if (!errorMessage || errorMessage === 'N/A') return null;

    const error = errorMessage.toLowerCase();

    if (error.includes('network') || error.includes('connection') || error.includes('timeout')) {
      return {
        icon: '🌐',
        color: 'text-orange-600',
        bgColor: 'bg-orange-100',
        category: 'Conectividade'
      };
    }

    if (error.includes('blocked') || error.includes('banned') || error.includes('spam')) {
      return {
        icon: '🚫',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        category: 'Bloqueio'
      };
    }

    if (error.includes('rate limit') || error.includes('quota') || error.includes('limit')) {
      return {
        icon: '⏰',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        category: 'Rate Limit'
      };
    }

    if (error.includes('invalid') || error.includes('not found') || error.includes('number')) {
      return {
        icon: '📱',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        category: 'Número Inválido'
      };
    }

    if (error.includes('permission') || error.includes('unauthorized') || error.includes('forbidden')) {
      return {
        icon: '🔒',
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        category: 'Permissão'
      };
    }

    if (error.includes('session') || error.includes('disconnected') || error.includes('logout')) {
      return {
        icon: '📲',
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-100',
        category: 'Sessão'
      };
    }

    if (error.includes('media') || error.includes('file') || error.includes('download')) {
      return {
        icon: '📁',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        category: 'Mídia'
      };
    }

    // Erro genérico
    return {
      icon: '⚠️',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      category: 'Erro Geral'
    };
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] bg-slate-50 px-6 py-8">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-8 w-44 animate-pulse rounded-xl bg-slate-200" />
              <div className="h-4 w-72 animate-pulse rounded-lg bg-slate-200" />
            </div>
            <div className="h-12 w-52 animate-pulse rounded-2xl bg-slate-200" />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="h-16 animate-pulse rounded-2xl border border-slate-200 bg-white" />
            ))}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="flex items-center justify-center gap-3 py-20 text-slate-500">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-slate-700" />
              <span className="text-sm font-medium">Carregando ações de envio...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header
        title="Campanhas"
        subtitle={missionId ? `${campaigns.length} campanhas vinculadas a esta missao` : `${campaigns.length} campanhas ativas`}
        actions={
          missionId ? (
            <Link
              to={`/missions/${missionId}/command-center`}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Voltar para a missao
            </Link>
          ) : (
            <div className="hidden" />
          )
        }
      />

      <div className="space-y-6 px-4 py-6 sm:px-6">
        {missionId && (
          <section className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-900 shadow-sm">
            VocÃª estÃ¡ vendo apenas as campanhas vinculadas Ã  missÃ£o atual.
          </section>
        )}

        <section className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => setCampaignsPageTab('actions')}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                  campaignsPageTab === 'actions'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Ações de envio
              </button>
              <button
                type="button"
                onClick={() => setCampaignsPageTab('templates')}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                  campaignsPageTab === 'templates'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Templates salvos
              </button>
              <button
                type="button"
                onClick={() => setCampaignsPageTab('usage')}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                  campaignsPageTab === 'usage'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Meu uso
              </button>
            </div>
            {campaignsPageTab === 'actions' && (
              <button
                onClick={openCreateModal}
                className="btn-primary"
                aria-label="Criar nova ação de envio"
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 4.167v11.666M4.167 10h11.666" />
                </svg>
                Nova ação de envio
              </button>
            )}
          </div>
        </section>

        {campaignsPageTab === 'actions' ? (
        <>
        <section className="rounded-3xl border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.12),_transparent_28%),linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Operação
              </span>
              <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                Ações de envio com visão executiva e controle rápido
              </h2>
              <p className="mt-2 text-sm leading-5 text-slate-600">
                Monitore desempenho, status e volume de disparos em um único lugar, com acesso rápido aos relatórios e às ações operacionais.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm text-slate-500 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                <div className="font-semibold text-slate-900">{campaigns.length}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">Campanhas</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                <div className="font-semibold text-slate-900">{totalContacts}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">Contatos</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm col-span-2 sm:col-span-1">
                <div className="font-semibold text-slate-900">{totalSent}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">Enviados</div>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-950">Ações de envio</h3>
              <p className="mt-1 text-sm text-slate-500">Acompanhe status, volume de contatos e sessões utilizadas em cada campanha.</p>
            </div>
            <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500">
              {campaigns.length} registro(s)
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {pageError && campaigns.length === 0 ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-rose-600 shadow-sm">
                  <svg className="h-6 w-6" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6.75v3.5m0 3h.008M17.25 10A7.25 7.25 0 1 1 2.75 10a7.25 7.25 0 0 1 14.5 0Z" />
                  </svg>
                </div>
                <h4 className="mt-4 text-lg font-semibold text-slate-900">Não foi possível carregar a lista</h4>
                <p className="mt-2 text-sm text-slate-500">{pageError}</p>
                <button type="button" onClick={loadCampaigns} className="btn-primary mt-6">
                  Tentar novamente
                </button>
              </div>
            ) : campaigns.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/70 px-6 py-16 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-white text-slate-400 shadow-sm">
                  <svg className="h-7 w-7" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 4.167v11.666M4.167 10h11.666" />
                  </svg>
                </div>
                <h4 className="mt-5 text-lg font-semibold text-slate-900">Nenhuma ação de envio criada</h4>
                <p className="mt-2 text-sm text-slate-500">
                  Crie sua primeira campanha para começar a organizar os disparos e acompanhar os resultados.
                </p>
                <button type="button" onClick={openCreateModal} className="btn-primary mt-6">
                  Nova ação de envio
                </button>
              </div>
            ) : (
              <CampaignTable
                campaigns={campaigns}
                onView={handleViewReport}
                onPause={(campaignId) => handleToggleCampaign(campaignId, 'pause')}
                onResume={(campaignId) => handleToggleCampaign(campaignId, 'resume')}
                onDelete={handleDeleteCampaign}
              />
            )}
          </div>
        </section>
        </>
        ) : campaignsPageTab === 'templates' ? (
          <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-5">
              <h3 className="text-lg font-semibold text-slate-950">Templates salvos</h3>
              <p className="mt-1 text-sm text-slate-500">Gerencie suas mensagens modelo e reutilize textos prontos nas novas campanhas.</p>
            </div>
            <div className="p-4 sm:p-6">
              <MessageTemplatesPanel />
            </div>
          </section>
        ) : (
          <div className="space-y-6">
            <section className="rounded-3xl border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.10),_transparent_28%),linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-2xl">
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Meu uso
                  </span>
                  <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                    Relatório diário e mensal de consumo
                  </h2>
                  <p className="mt-2 text-sm leading-5 text-slate-600">
                    Acompanhe quanto do seu plano já foi utilizado em WhatsApp e E-mail, com visão rápida do dia e do ciclo mensal.
                  </p>
                </div>
                {usageSummary && (
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Plano atual</div>
                    <div className="mt-1 text-lg font-semibold text-slate-950">{usageSummary.planTitle}</div>
                    <div className="mt-1 text-sm text-slate-500">Competência {usageSummary.reset.monthlyCompetence}</div>
                  </div>
                )}
              </div>
            </section>

            {usageSummary ? (
              <>
                <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-3xl border border-emerald-200 bg-emerald-50/70 p-5 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">WhatsApp no mês</p>
                    <div className="mt-3 text-2xl font-semibold text-emerald-950">
                      {formatMetricNumber(whatsappUsage?.usedMonth || 0)}
                    </div>
                    <p className="mt-2 text-sm text-emerald-800">
                      Limite: {formatMetricNumber(whatsappUsage?.monthlyLimit ?? null)}
                    </p>
                  </div>

                  <div className="rounded-3xl border border-emerald-200 bg-white p-5 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">WhatsApp hoje</p>
                    <div className="mt-3 text-2xl font-semibold text-slate-950">
                      {formatMetricNumber(whatsappUsage?.usedToday || 0)}
                    </div>
                    <p className="mt-2 text-sm text-slate-600">
                      Limite diário: {formatMetricNumber(whatsappUsage?.dailyLimit ?? null)}
                    </p>
                  </div>

                  <div className="rounded-3xl border border-amber-200 bg-amber-50/70 p-5 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">E-mail no mês</p>
                    <div className="mt-3 text-2xl font-semibold text-amber-950">
                      {formatMetricNumber(emailUsage?.usedMonth || 0)}
                    </div>
                    <p className="mt-2 text-sm text-amber-800">
                      Limite: {formatMetricNumber(emailUsage?.monthlyLimit ?? null)}
                    </p>
                  </div>

                  <div className="rounded-3xl border border-amber-200 bg-white p-5 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">E-mail hoje</p>
                    <div className="mt-3 text-2xl font-semibold text-slate-950">
                      {formatMetricNumber(emailUsage?.usedToday || 0)}
                    </div>
                    <p className="mt-2 text-sm text-slate-600">
                      Limite diário: {formatMetricNumber(emailUsage?.dailyLimit ?? null)}
                    </p>
                  </div>
                </section>

                <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-200 px-6 py-5">
                    <h3 className="text-lg font-semibold text-slate-950">Resumo por canal</h3>
                    <p className="mt-1 text-sm text-slate-500">Leitura consolidada do seu consumo diário e mensal por canal.</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Canal</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Hoje</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Limite diário</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">No mês</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Limite mensal</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <tr>
                          <td className="px-6 py-4 text-sm font-semibold text-emerald-900">WhatsApp</td>
                          <td className="px-6 py-4 text-sm text-slate-700">{formatMetricNumber(whatsappUsage?.usedToday || 0)}</td>
                          <td className="px-6 py-4 text-sm text-slate-700">{formatMetricNumber(whatsappUsage?.dailyLimit ?? null)}</td>
                          <td className="px-6 py-4 text-sm text-slate-700">{formatMetricNumber(whatsappUsage?.usedMonth || 0)}</td>
                          <td className="px-6 py-4 text-sm text-slate-700">{formatMetricNumber(whatsappUsage?.monthlyLimit ?? null)}</td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 text-sm font-semibold text-amber-900">E-mail</td>
                          <td className="px-6 py-4 text-sm text-slate-700">{formatMetricNumber(emailUsage?.usedToday || 0)}</td>
                          <td className="px-6 py-4 text-sm text-slate-700">{formatMetricNumber(emailUsage?.dailyLimit ?? null)}</td>
                          <td className="px-6 py-4 text-sm text-slate-700">{formatMetricNumber(emailUsage?.usedMonth || 0)}</td>
                          <td className="px-6 py-4 text-sm text-slate-700">{formatMetricNumber(emailUsage?.monthlyLimit ?? null)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            ) : (
              <section className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-slate-950">Consumo indisponível agora</h3>
                  <p className="mt-2 text-sm text-slate-500">
                    Não foi possível carregar seu relatório de uso neste momento. Tente novamente em instantes.
                  </p>
                </div>
              </section>
            )}
          </div>
        )}

        {/* Modal de Criação */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-30 p-4 sm:p-6 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[calc(100vh-2rem)] overflow-y-auto">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4 sm:p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Nova Ação de envio</h3>
                  <p className="text-sm text-gray-600 mt-1">Configure sua ação de envio de mensagens</p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateStep('channel');
                  }}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {createStep === 'channel' ? (
                <div className="p-4 sm:p-6 space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">Escolha o canal da ação</h4>
                    <p className="text-sm text-gray-600 mt-1">Selecione se esta ação será preparada para Email ou WhatsApp.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => handleChannelSelection(['WHATSAPP'])}
                      className="text-left p-5 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                    >
                      <div className="text-sm font-semibold text-emerald-900">WhatsApp</div>
                      <p className="text-sm text-emerald-800 mt-2">Mantém o fluxo atual de mensagens, com seleção de conexões e sequência de conteúdo.</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChannelSelection(['EMAIL'])}
                      className="text-left p-5 rounded-xl border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors"
                    >
                      <div className="text-sm font-semibold text-amber-900">Email</div>
                      <p className="text-sm text-amber-800 mt-2">Abre a preparação da campanha de email com assunto e conteúdo dinâmico por contato.</p>
                    </button>
                  </div>
                </div>
              ) : (
              <form onSubmit={handleCreateCampaign} className="p-4 sm:p-6">
                <div className="mb-6">
                  <div className="inline-flex rounded-full border border-slate-200 bg-slate-100 p-1">
                    <button
                      type="button"
                      onClick={() => handleChannelSelection(['WHATSAPP'])}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${hasWhatsappChannel ? 'bg-white text-emerald-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                    >
                      WhatsApp
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChannelSelection(['EMAIL'])}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${hasEmailChannel ? 'bg-white text-amber-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                    >
                      Email
                    </button>
                  </div>
                </div>
                {usageSummary && (
                  <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <div className={`rounded-2xl border p-4 ${hasWhatsappChannel ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Consumo atual WhatsApp</div>
                      <div className="mt-2 text-base font-semibold text-slate-950">
                        {formatUsageText(whatsappUsage?.usedMonth || 0, whatsappUsage?.monthlyLimit ?? null, 'mensagem', 'mensagens')}
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        Hoje: {formatUsageText(whatsappUsage?.usedToday || 0, whatsappUsage?.dailyLimit ?? null, 'mensagem', 'mensagens')}
                      </div>
                    </div>
                    <div className={`rounded-2xl border p-4 ${hasEmailChannel ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Consumo atual E-mail</div>
                      <div className="mt-2 text-base font-semibold text-slate-950">
                        {formatUsageText(emailUsage?.usedMonth || 0, emailUsage?.monthlyLimit ?? null, 'e-mail', 'e-mails')}
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        Hoje: {formatUsageText(emailUsage?.usedToday || 0, emailUsage?.dailyLimit ?? null, 'e-mail', 'e-mails')}
                      </div>
                    </div>
                  </div>
                )}
                <div className={`grid grid-cols-1 ${hasWhatsappChannel ? 'lg:grid-cols-2' : ''} gap-8`}>

                  {/* COLUNA ESQUERDA - Informações Básicas */}
                  <div className="space-y-6">
                    {/* <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <h4 className="text-lg font-semibold text-blue-900 mb-2">Informações Básicas</h4>
                      <p className="text-sm text-blue-700">Configure os dados fundamentais da sua campanha</p>
                    </div> */}

                    {/* Nome da Campanha */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome da Campanha *
                      </label>
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ex: Mandados do mês de Junho"
                        required
                      />
                    </div>

                    {/* Categorias de Contatos */}
                    <div className={hasEmailChannel ? 'rounded-xl border border-amber-200 bg-amber-50/40 p-4' : ''}>
                      {hasEmailChannel && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-amber-900">Categoria de envio</h4>
                          <p className="mt-1 text-xs text-amber-800">
                            Escolha abaixo quais categorias de contatos serão os destinatários do envio por Email.
                          </p>
                        </div>
                      )}
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {hasEmailChannel ? 'Categoria de envio *' : 'Categorias de Contatos *'}
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        {hasEmailChannel
                          ? 'Selecione a categoria de envio para definir quem receberá este email'
                          : 'Selecione quais categorias de contatos receberão a campanha'}
                      </p>
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                        {contactTags.map((tag) => (
                          <label key={tag.id} className="flex items-center space-x-2 mb-2 p-2 rounded hover:bg-white cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.targetTags.includes(tag.id)}
                              onChange={() => handleTagToggle(tag.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 font-medium">{tag.nome}</span>
                          </label>
                        ))}
                        {contactTags.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma categoria disponível</p>
                        )}
                      </div>
                      {hasEmailChannel && formData.targetTags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {contactTags
                            .filter((tag) => formData.targetTags.includes(tag.id))
                            .map((tag) => (
                              <span
                                key={tag.id}
                                className="inline-flex items-center rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-medium text-amber-800"
                              >
                                {tag.nome}
                              </span>
                            ))}
                        </div>
                      )}
                      {formData.targetTags.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">✅ {formData.targetTags.length} categoria(s) selecionada(s)</p>
                      )}
                    </div>

                    {hasEmailChannel && (
                      <div className="space-y-4">
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                          <h4 className="text-sm font-semibold text-amber-900">Preparação do Email</h4>
                          <p className="text-xs text-amber-800 mt-1">Use variáveis como {'{{nome}}'}, {'{{email}}'}, {'{{telefone}}'}, {'{{bairro}}'} e {'{{cidade}}'} para personalizar o envio.</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assunto do Email *
                          </label>
                          <input
                            type="text"
                            value={formData.emailSubject}
                            onChange={(e) => setFormData(prev => ({ ...prev, emailSubject: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                            placeholder="Ex: Atualização importante sobre seu atendimento"
                            required={hasEmailChannel}
                          />
                        </div>

                        <div className="space-y-3">
                          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                              <div>
                                <p className="text-sm font-medium text-amber-900">Mensagens salvas</p>
                                <p className="text-xs text-amber-800">Use um template salvo para preencher o conteúdo do email.</p>
                              </div>
                            </div>

                            <div className="mt-3 space-y-3">
                              <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                  Modelos salvos
                                </label>
                                <select
                                  value={selectedSavedTemplateId}
                                  onChange={(e) => setSelectedSavedTemplateId(e.target.value)}
                                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Selecione um modelo salvo</option>
                                  {savedTemplates.map((template) => (
                                    <option key={template.id} value={template.id}>
                                      {template.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={handleUseSavedTemplateForEmail}
                                  disabled={!selectedSavedTemplateId}
                                  className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  Utilizar Mensagem salva
                                </button>
                              </div>

                              {selectedSavedTemplateId && savedTemplates.find(t => t.id === selectedSavedTemplateId) && (
                                <div className="rounded-lg border border-amber-100 bg-white p-3 text-xs text-gray-600">
                                  <p className="font-medium text-amber-900">
                                    {savedTemplates.find(t => t.id === selectedSavedTemplateId)?.name || 'Modelo salvo'}
                                  </p>
                                  <p className="mt-1">
                                    {savedTemplates.find(t => t.id === selectedSavedTemplateId)?.description || 'Mensagem pronta para reutilização na campanha.'}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Conteúdo do Email *
                            </label>
                            <div className="overflow-hidden rounded-xl border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-amber-500">
                              <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
                                <button
                                  type="button"
                                  onClick={() => handleApplyEmailFormatting('bold')}
                                  className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                                >
                                  Negrito
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleApplyEmailFormatting('italic')}
                                  className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                                >
                                  Itálico
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleApplyEmailFormatting('insertUnorderedList')}
                                  className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                                >
                                  Lista
                                </button>
                                <label className="inline-flex cursor-pointer items-center rounded-md border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100">
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handleInlineEmailImageUpload(file);
                                        e.currentTarget.value = '';
                                      }
                                    }}
                                  />
                                  Inserir imagem no conteúdo
                                </label>
                              </div>

                              <div
                                ref={emailBodyEditorRef}
                                contentEditable
                                suppressContentEditableWarning
                                onInput={handleEmailBodyInput}
                                className="min-h-[220px] w-full px-3 py-3 text-sm text-gray-800 focus:outline-none [&_img]:my-3 [&_img]:block [&_img]:max-w-full [&_img]:rounded-xl [&_img]:border [&_img]:border-amber-100 [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
                                data-placeholder="Escreva aqui a mensagem que será enviada por email. Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{bairro}} e {{cidade}} para personalizar."
                              ></div>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                              Imagens inseridas aqui seguem dentro do corpo do email. A seção &quot;Mídia no Email&quot; abaixo continua enviando arquivos como anexo.
                            </p>
                            {Object.entries(uploadingFiles).some(([key, value]) => key.startsWith('email-inline-image-') && value) && (
                              <p className="mt-2 text-xs text-amber-700">Enviando imagem para o conteúdo do email...</p>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3 rounded-xl border border-amber-200 bg-white p-4">
                          <div>
                            <h5 className="text-sm font-semibold text-gray-900">Mídia no Email</h5>
                            <p className="mt-1 text-xs text-gray-500">Inclua imagem e/ou vídeo para complementar o conteúdo do email.</p>
                          </div>

                          <div className="flex flex-wrap gap-3">
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100">
                              <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleEmailMediaUpload(file, 'image');
                                    e.currentTarget.value = '';
                                  }
                                }}
                              />
                              Adicionar imagem
                            </label>

                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100">
                              <input
                                type="file"
                                className="hidden"
                                accept="video/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleEmailMediaUpload(file, 'video');
                                    e.currentTarget.value = '';
                                  }
                                }}
                              />
                              Adicionar vídeo
                            </label>
                          </div>

                          {Object.entries(uploadingFiles).some(([key, value]) => key.startsWith('email-') && value) && (
                            <p className="text-xs text-amber-700">Enviando mídia do email...</p>
                          )}

                          {formData.emailMedia.length > 0 && (
                            <div className="grid gap-3 md:grid-cols-2">
                              {formData.emailMedia.map((media, mediaIndex) => (
                                <div key={`${media.url}-${mediaIndex}`} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-gray-900 truncate">{media.fileName}</div>
                                      <div className="text-xs text-gray-500 uppercase">{media.type === 'image' ? 'Imagem' : 'Vídeo'}</div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveEmailMedia(mediaIndex)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      Remover
                                    </button>
                                  </div>

                                  <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-black/5">
                                    {media.type === 'image' ? (
                                      <img src={media.url} alt={media.fileName} className="h-40 w-full object-cover" />
                                    ) : (
                                      <video src={media.url} className="h-40 w-full object-cover" controls />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Conexões WhatsApp */}
                    {hasWhatsappChannel && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Conexões WhatsApp * ({formData.sessionNames.length} selecionadas)
                      </label>
                      {/* <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <h4 className="text-sm font-medium text-green-800">Multi-Sessão com Failover</h4>
                            <p className="text-sm text-green-700 mt-1">
                              Sistema inteligente que distribui envios entre múltiplas conexões com redundância automática.
                            </p>
                          </div>
                        </div>
                      </div> */}
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50">
                        {whatsappSessions.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">Nenhuma conexão WhatsApp disponível</p>
                        ) : (
                          whatsappSessions.map((session) => (
                            <label key={session.name} className="flex items-center justify-between space-x-2 mb-2 p-2 rounded hover:bg-white cursor-pointer">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={formData.sessionNames.includes(session.name)}
                                  onChange={() => handleSessionToggle(session.name)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                  <span className="text-sm font-medium text-gray-700">
                                    {session.mePushName || session.displayName || session.name}
                                  </span>
                                  <span className="text-xs text-gray-500 block">({session.displayName || session.name})</span>
                                </div>
                              </div>
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                Ativa
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                      {formData.sessionNames.length > 0 && (
                        <p className="text-xs text-green-600 mt-2">✅ {formData.sessionNames.length} conexão(ões) selecionada(s)</p>
                      )}
                    </div>
                    )}

                    {/* Configuração de Envio */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Intervalo Mínimo entre Envios (segundos)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="300"
                        value={formData.randomDelay}
                        onChange={(e) => setFormData(prev => ({ ...prev, randomDelay: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        ⏱️ Aguarda pelo menos {formData.randomDelay}s entre envios pela mesma conexão
                      </p>
                    </div>

                    {/* Data de Envio */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quando Enviar
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.startImmediately}
                            onChange={() => setFormData(prev => ({ ...prev, startImmediately: true }))}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700">🚀 Iniciar imediatamente</span>
                            <p className="text-xs text-gray-500">A campanha será executada assim que for criada</p>
                          </div>
                        </label>
                        <label className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio"
                            checked={!formData.startImmediately}
                            onChange={() => setFormData(prev => ({ ...prev, startImmediately: false }))}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700">📅 Agendar para:</span>
                            <p className="text-xs text-gray-500">Escolha data e hora específicas</p>
                          </div>
                        </label>
                        {!formData.startImmediately && (
                          <input
                            type="datetime-local"
                            value={formData.scheduledFor}
                            onChange={(e) => setFormData(prev => ({ ...prev, scheduledFor: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ml-6"
                            required={!formData.startImmediately}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* COLUNA DIREITA - Mensagens */}
                  <div className={hasWhatsappChannel ? 'space-y-6' : 'hidden'}>
                    {/* <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <h4 className="text-lg font-semibold text-purple-900 mb-2">💬 Mensagens da Campanha</h4>
                      <p className="text-sm text-purple-700">Configure o conteúdo que será enviado aos contatos</p>
                    </div> */}

                    <div>
                      {/* <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <h4 className="text-sm font-medium text-blue-800">💡 Variáveis Dinâmicas</h4>
                            <p className="text-sm text-blue-700 mt-1">
                              Personalize suas mensagens com:
                              <span className="block mt-1">
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{nome}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{cpf}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{telefone}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{bairro}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{cidade}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{email}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{categoria}}'}</code>
                                <code className="bg-blue-100 px-1 rounded mx-1">{'{{observacoes}}'}</code>
                              </span>
                            </p>
                          </div>
                        </div>
                      </div> */}

                      <div className="space-y-4">
                        <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <p className="text-sm text-gray-600">Adicione uma ou mais mensagens para serem enviadas em sequência</p>
                            <button
                              type="button"
                              onClick={() => appendTextMessageToSequence('')}
                              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center justify-center gap-2"
                            >
                              <span className="text-lg">+</span>
                              Mensagem
                            </button>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {('sequence' in formData.messageContent) && formData.messageContent.sequence.map((item, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-medium text-gray-600">Mensagem {index + 1}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                    const newSequence = currentSequence.filter((_, i) => i !== index);
                                    setFormData(prev => ({
                                      ...prev,
                                      messageContent: { sequence: newSequence }
                                    }));
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Remover
                                </button>
                              </div>

                              <div className="space-y-3">
                                <select
                                  value={item.type}
                                  onChange={(e) => {
                                    const newType = e.target.value;
                                    let newContent;
                                    switch (newType) {
                                      case 'text':
                                        newContent = { text: '' };
                                        break;
                                      case 'document':
                                        newContent = { url: '', fileName: '' };
                                        break;
                                      case 'openai':
                                        newContent = { model: '', system: '', user: '' };
                                        break;
                                      case 'groq':
                                        newContent = { model: '', system: '', user: '' };
                                        break;
                                      default:
                                        newContent = { url: '', caption: '' };
                                        break;
                                    }

                                    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                    const newSequence = currentSequence.map((seqItem, i) =>
                                      i === index ? { type: newType, content: newContent } : seqItem
                                    );
                                    setFormData(prev => ({
                                      ...prev,
                                      messageContent: { sequence: newSequence }
                                    }));
                                    setActiveSavedTemplateMessageIndex(newType === 'text' ? index : null);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="text">Texto</option>
                                  <option value="image">Imagem</option>
                                  <option value="video">Vídeo</option>
                                  <option value="audio">Áudio</option>
                                  <option value="document">Arquivo</option>
                                  <option value="openai">OpenAI</option>
                                  <option value="groq">Groq AI</option>
                                </select>

                                {item.type === 'text' && (
                                  <div className="space-y-2">
                                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
                                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                        <div>
                                          <p className="text-sm font-medium text-emerald-900">Mensagens salvas</p>
                                          <p className="text-xs text-emerald-800">Use um template salvo para preencher esta mensagem de texto.</p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => setActiveSavedTemplateMessageIndex(
                                            activeSavedTemplateMessageIndex === index ? null : index
                                          )}
                                          className="rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
                                        >
                                          {activeSavedTemplateMessageIndex === index ? 'Ocultar mensagens salvas' : 'Chamar mensagens salvas'}
                                        </button>
                                      </div>

                                      {activeSavedTemplateMessageIndex === index && (
                                        <div className="mt-3 space-y-3">
                                          <div>
                                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                              Modelos salvos
                                            </label>
                                            <select
                                              value={selectedSavedTemplateId}
                                              onChange={(e) => setSelectedSavedTemplateId(e.target.value)}
                                              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                              <option value="">Selecione um modelo salvo</option>
                                              {savedTemplates.map((template) => (
                                                <option key={template.id} value={template.id}>
                                                  {template.name}
                                                </option>
                                              ))}
                                            </select>
                                          </div>

                                          <div className="flex justify-end">
                                            <button
                                              type="button"
                                              onClick={() => handleUseSavedTemplateInSequence(index)}
                                              disabled={!selectedSavedTemplateId}
                                              className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                              Utilizar Mensagem salva
                                            </button>
                                          </div>

                                          {selectedSavedTemplateId && (
                                            <div className="rounded-lg border border-emerald-100 bg-white p-3 text-xs text-gray-600">
                                              <p className="font-medium text-emerald-900">
                                                {selectedSavedTemplate?.name || 'Modelo salvo'}
                                              </p>
                                              <p className="mt-1">
                                                {selectedSavedTemplate?.description || 'Mensagem pronta para reutilização na campanha.'}
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    {/* Checkbox para usar variações */}
                                    <div className="flex items-center space-x-2">
                                      <input
                                        type="checkbox"
                                        id={`useVariations-${index}`}
                                        checked={item.content.useVariations || false}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: {
                                                ...seqItem.content,
                                                useVariations: e.target.checked,
                                                variations: e.target.checked ? [''] : undefined,
                                                text: e.target.checked ? undefined : seqItem.content.text
                                              }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="rounded text-blue-600 focus:ring-blue-500"
                                      />
                                      <label htmlFor={`useVariations-${index}`} className="text-sm font-medium text-gray-700">
                                        Usar variações de texto
                                      </label>
                                    </div>

                                    {item.content.useVariations ? (
                                      /* Modo variações */
                                      <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                          Variações de texto
                                        </label>
                                        {(item.content.variations || ['']).map((variation: string, varIndex: number) => (
                                          <div key={varIndex} className="flex gap-2">
                                            <div className="flex-1">
                                              <textarea
                                                placeholder={`Variação ${varIndex + 1}... Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{bairro}}, {{cidade}}, {{categoria}}, {{observacoes}}`}
                                                value={variation}
                                                onChange={(e) => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) => {
                                                    if (i === index) {
                                                      const newVariations = [...(seqItem.content.variations || [])];
                                                      newVariations[varIndex] = e.target.value;
                                                      return {
                                                        ...seqItem,
                                                        content: { ...seqItem.content, variations: newVariations }
                                                      };
                                                    }
                                                    return seqItem;
                                                  });
                                                  setFormData(prev => ({
                                                    ...prev,
                                                    messageContent: { sequence: newSequence }
                                                  }));
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                rows={2}
                                              />
                                            </div>
                                            {(item.content.variations || []).length > 1 && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                  const newSequence = currentSequence.map((seqItem, i) => {
                                                    if (i === index) {
                                                      const newVariations = [...(seqItem.content.variations || [])];
                                                      newVariations.splice(varIndex, 1);
                                                      return {
                                                        ...seqItem,
                                                        content: { ...seqItem.content, variations: newVariations }
                                                      };
                                                    }
                                                    return seqItem;
                                                  });
                                                  setFormData(prev => ({
                                                    ...prev,
                                                    messageContent: { sequence: newSequence }
                                                  }));
                                                }}
                                                className="px-2 py-1 text-red-600 hover:text-red-800"
                                                title="Remover variação"
                                              >
                                                ✕
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) => {
                                              if (i === index) {
                                                const newVariations = [...(seqItem.content.variations || []), ''];
                                                return {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, variations: newVariations }
                                                };
                                              }
                                              return seqItem;
                                            });
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded hover:bg-blue-200 flex items-center gap-1"
                                        >
                                          <span>+</span>
                                          Nova variação
                                        </button>
                                      </div>
                                    ) : (
                                      /* Modo texto único */
                                      <div>
                                        <textarea
                                          placeholder="Digite sua mensagem... Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{bairro}}, {{cidade}}, {{categoria}}, {{observacoes}}"
                                          value={item.content.text || ''}
                                          onChange={(e) => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) =>
                                              i === index ? { ...seqItem, content: { text: e.target.value } } : seqItem
                                            );
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          rows={3}
                                        />
                                      </div>
                                    )}

                                    <div className="flex flex-wrap gap-1">
                                      <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                      {['{{nome}}', '{{email}}', '{{cpf}}', '{{telefone}}', '{{bairro}}', '{{cidade}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                        <button
                                          key={variable}
                                          type="button"
                                          onClick={() => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            if (item.content.useVariations) {
                                              // Adicionar à primeira variação vazia ou criar nova
                                              const variations = item.content.variations || [''];
                                              let targetIndex = variations.findIndex(v => !v.trim());
                                              if (targetIndex === -1) targetIndex = 0;

                                              const newSequence = currentSequence.map((seqItem, i) => {
                                                if (i === index) {
                                                  const newVariations = [...variations];
                                                  newVariations[targetIndex] = (newVariations[targetIndex] || '') + variable;
                                                  return {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, variations: newVariations }
                                                  };
                                                }
                                                return seqItem;
                                              });
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            } else {
                                              // Adicionar ao texto único
                                              const currentText = currentSequence[index]?.content?.text || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? { ...seqItem, content: { text: currentText + variable } } : seqItem
                                              );
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            }
                                          }}
                                          className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                                        >
                                          {variable}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {item.type === 'openai' && (
                                  <div className="space-y-4">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-blue-600 font-medium">🤖 OpenAI</span>
                                      </div>
                                      <p className="text-sm text-blue-600">
                                        Configure os parâmetros para gerar mensagens personalizadas usando inteligência artificial
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Modelo *
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="Digite o modelo da OpenAI (ex: gpt-3.5-turbo)"
                                        value={item.content.model || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, model: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <div className="mt-2">
                                        <p className="text-xs text-gray-500 mb-1">Modelos sugeridos:</p>
                                        <div className="flex flex-wrap gap-1">
                                          {['gpt-3.5-turbo', 'gpt-4', 'gpt-4-turbo', 'gpt-4o', 'gpt-4o-mini'].map((model) => (
                                            <button
                                              key={model}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, model: model }
                                                  } : seqItem
                                                );
                                                setFormData(prev => ({
                                                  ...prev,
                                                  messageContent: { sequence: newSequence }
                                                }));
                                              }}
                                              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 border border-gray-300"
                                            >
                                              {model}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Sistema *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                        value={item.content.system || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, system: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={3}
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        Define como a IA deve se comportar e responder
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Usuário *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}. Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                        value={item.content.user || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, user: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={4}
                                      />
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                        {['{{nome}}', '{{email}}', '{{cpf}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const currentUser = currentSequence[index]?.content?.user || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, user: currentUser + variable }
                                                } : seqItem
                                              );
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            }}
                                            className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded hover:bg-green-200"
                                          >
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">
                                        A IA usará este prompt junto com os dados do contato para gerar a mensagem
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {item.type === 'groq' && (
                                  <div className="space-y-4">
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-yellow-600 font-medium">⚡ Groq AI</span>
                                      </div>
                                      <p className="text-sm text-yellow-600">
                                        Configure os parâmetros para gerar mensagens personalizadas usando Groq AI com velocidade ultra-rápida
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Modelo *
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="Digite o modelo do Groq (ex: llama-3.1-8b-instant)"
                                        value={item.content.model || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, model: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <div className="mt-2">
                                        <p className="text-xs text-gray-500 mb-1">Modelos sugeridos:</p>
                                        <div className="flex flex-wrap gap-1">
                                          {['llama-3.1-8b-instant', 'llama-3.1-70b-versatile', 'llama-3.2-11b-text-preview', 'mixtral-8x7b-32768', 'gemma2-9b-it'].map((model) => (
                                            <button
                                              key={model}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, model: model }
                                                  } : seqItem
                                                );
                                                setFormData(prev => ({
                                                  ...prev,
                                                  messageContent: { sequence: newSequence }
                                                }));
                                              }}
                                              className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded hover:bg-yellow-200 border border-yellow-300"
                                            >
                                              {model}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Sistema *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Você é um assistente virtual especializado em vendas. Responda de forma profissional e persuasiva..."
                                        value={item.content.system || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, system: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={3}
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        Define como a IA deve se comportar e responder
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Prompt do Usuário *
                                      </label>
                                      <textarea
                                        placeholder="Ex: Crie uma mensagem de vendas personalizada para {{nome}}, que tem interesse em {{categoria}}. Use variáveis como {{nome}}, {{email}}, {{telefone}}, {{categoria}}, {{observacoes}}"
                                        value={item.content.user || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, user: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={4}
                                      />
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        <span className="text-xs text-gray-500">Variáveis disponíveis:</span>
                                        {['{{nome}}', '{{email}}', '{{cpf}}', '{{telefone}}', '{{categoria}}', '{{observacoes}}'].map((variable) => (
                                          <button
                                            key={variable}
                                            type="button"
                                            onClick={() => {
                                              const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                              const currentUser = currentSequence[index]?.content?.user || '';
                                              const newSequence = currentSequence.map((seqItem, i) =>
                                                i === index ? {
                                                  ...seqItem,
                                                  content: { ...seqItem.content, user: currentUser + variable }
                                                } : seqItem
                                              );
                                              setFormData(prev => ({
                                                ...prev,
                                                messageContent: { sequence: newSequence }
                                              }));
                                            }}
                                            className="px-2 py-1 bg-orange-100 text-orange-600 text-xs rounded hover:bg-orange-200"
                                          >
                                            {variable}
                                          </button>
                                        ))}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">
                                        A IA usará este prompt junto com os dados do contato para gerar a mensagem
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {['image', 'video', 'audio', 'document'].includes(item.type) && (
                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <label className="block text-sm font-medium text-gray-700">
                                        Arquivo
                                      </label>
                                      <label className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={item.content.useMediaVariations || false}
                                          onChange={(e) => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) => {
                                              if (i === index) {
                                                return {
                                                  ...seqItem,
                                                  content: {
                                                    ...seqItem.content,
                                                    useMediaVariations: e.target.checked,
                                                    mediaVariations: e.target.checked ? Array.from({ length: 4 }, () => ({ url: '', caption: '', fileName: '' })) : undefined,
                                                    // Limpar URL principal quando ativar variações
                                                    url: e.target.checked ? '' : seqItem.content.url
                                                  }
                                                };
                                              }
                                              return seqItem;
                                            });
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-600">Usar Variações</span>
                                      </label>
                                    </div>

                                    {item.content.useMediaVariations ? (
                                      // Grid horizontal de 4 variações
                                      <div className="grid grid-cols-4 gap-3">
                                        {Array.from({ length: 4 }, (_, varIndex) => {
                                          const mediaVariations = item.content.mediaVariations || [];
                                          const variation = mediaVariations[varIndex] || { url: '', caption: '', fileName: '' };
                                          const hasFile = variation.url;

                                          return (
                                            <div key={varIndex} className="space-y-2">
                                              {/* Header da variação */}
                                              <div className="flex items-center justify-between">
                                                <span className="text-xs font-medium text-gray-600">
                                                  {varIndex === 0 ? 'Principal' : `Var. ${varIndex + 1}`}
                                                </span>
                                                {hasFile && varIndex > 0 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                      const newSequence = currentSequence.map((seqItem, i) => {
                                                        if (i === index) {
                                                          const newVariations = [...(seqItem.content.mediaVariations || [])];
                                                          newVariations[varIndex] = { url: '', caption: '', fileName: '' };
                                                          return {
                                                            ...seqItem,
                                                            content: { ...seqItem.content, mediaVariations: newVariations }
                                                          };
                                                        }
                                                        return seqItem;
                                                      });
                                                      setFormData(prev => ({
                                                        ...prev,
                                                        messageContent: { sequence: newSequence }
                                                      }));
                                                    }}
                                                    className="text-red-500 hover:text-red-700 text-xs p-0.5"
                                                    title="Remover"
                                                  >
                                                    ✕
                                                  </button>
                                                )}
                                              </div>

                                              {/* Preview quadrado */}
                                              <div className="aspect-square border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                                                {!hasFile ? (
                                                  <>
                                                    <input
                                                      type="file"
                                                      id={`file-upload-${index}-${varIndex}`}
                                                      className="hidden"
                                                      accept={
                                                        item.type === 'image' ? 'image/*' :
                                                        item.type === 'video' ? 'video/*' :
                                                        item.type === 'audio' ? 'audio/*' :
                                                        'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                                      }
                                                      onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                          handleFileUpload(file, index, varIndex);
                                                        }
                                                      }}
                                                      disabled={uploadingFiles[`${index}-${varIndex}`]}
                                                    />
                                                    <label
                                                      htmlFor={`file-upload-${index}-${varIndex}`}
                                                      className={`w-full h-full flex flex-col items-center justify-center cursor-pointer transition-colors hover:border-blue-400 hover:bg-blue-50 ${
                                                        uploadingFiles[`${index}-${varIndex}`]
                                                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                                                          : 'text-gray-500 hover:text-blue-600'
                                                      }`}
                                                    >
                                                      {uploadingFiles[`${index}-${varIndex}`] ? (
                                                        <div className="flex flex-col items-center gap-1">
                                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                          <span className="text-xs">Enviando</span>
                                                        </div>
                                                      ) : (
                                                        <div className="flex flex-col items-center gap-1">
                                                          <div className="text-2xl">
                                                            {item.type === 'image' && '🖼️'}
                                                            {item.type === 'video' && '🎥'}
                                                            {item.type === 'audio' && '🎵'}
                                                            {item.type === 'document' && '📄'}
                                                          </div>
                                                          <span className="text-xs text-center">
                                                            Fazer upload
                                                          </span>
                                                        </div>
                                                      )}
                                                    </label>
                                                  </>
                                                ) : (
                                                  <div className="w-full h-full relative group">
                                                    {item.type === 'image' && (
                                                      <img
                                                        src={variation.url}
                                                        alt={`Variação ${varIndex + 1}`}
                                                        className="w-full h-full object-cover"
                                                      />
                                                    )}

                                                    {item.type === 'video' && (
                                                      <div className="w-full h-full relative">
                                                        <video
                                                          src={variation.url}
                                                          className="w-full h-full object-cover"
                                                          muted
                                                        />
                                                        <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center">
                                                          <div className="bg-white bg-opacity-90 p-1 rounded-full">
                                                            <svg className="w-4 h-4 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
                                                              <path d="M8 5v14l11-7z"/>
                                                            </svg>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    )}

                                                    {item.type === 'audio' && (
                                                      <div className="w-full h-full bg-gradient-to-br from-green-100 to-green-200 flex flex-col items-center justify-center">
                                                        <div className="text-3xl mb-1">🎵</div>
                                                        <div className="text-xs text-center text-gray-700 px-1">
                                                          Áudio
                                                        </div>
                                                      </div>
                                                    )}

                                                    {item.type === 'document' && (
                                                      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex flex-col items-center justify-center p-2">
                                                        <div className="text-3xl mb-1">📄</div>
                                                        <div className="text-xs text-center text-gray-700 truncate w-full">
                                                          {variation.fileName || 'Documento'}
                                                        </div>
                                                      </div>
                                                    )}

                                                    {/* Overlay de ações */}
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                      <a
                                                        href={variation.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="bg-white bg-opacity-90 px-2 py-1 rounded text-xs font-medium"
                                                      >
                                                        Ver
                                                      </a>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>

                                              {/* Campo de legenda para imagem e vídeo */}
                                              {hasFile && ['image', 'video'].includes(item.type) && (
                                                <input
                                                  type="text"
                                                  placeholder="Legenda..."
                                                  value={variation.caption || ''}
                                                  onChange={(e) => {
                                                    const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                    const newSequence = currentSequence.map((seqItem, i) => {
                                                      if (i === index) {
                                                        const newVariations = [...(seqItem.content.mediaVariations || [])];
                                                        // Garantir que o array tenha o tamanho necessário
                                                        while (newVariations.length <= varIndex) {
                                                          newVariations.push({ url: '', caption: '', fileName: '' });
                                                        }
                                                        newVariations[varIndex] = { ...newVariations[varIndex], caption: e.target.value };
                                                        return {
                                                          ...seqItem,
                                                          content: { ...seqItem.content, mediaVariations: newVariations }
                                                        };
                                                      }
                                                      return seqItem;
                                                    });
                                                    setFormData(prev => ({
                                                      ...prev,
                                                      messageContent: { sequence: newSequence }
                                                    }));
                                                  }}
                                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                />
                                              )}

                                              {/* Nome do arquivo para não-imagens */}
                                              {hasFile && !['image', 'video'].includes(item.type) && (
                                                <div className="text-xs text-gray-600 truncate" title={variation.fileName}>
                                                  {variation.fileName || 'Arquivo'}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      // Interface original de arquivo único
                                      <>
                                        {!item.content.url ? (
                                          <div className="flex items-center gap-3">
                                            <input
                                              type="file"
                                              id={`file-upload-${index}`}
                                              className="hidden"
                                              accept={
                                                item.type === 'image' ? 'image/*' :
                                                item.type === 'video' ? 'video/*' :
                                                item.type === 'audio' ? 'audio/*' :
                                                'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
                                              }
                                              onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                  handleFileUpload(file, index);
                                                }
                                              }}
                                              disabled={uploadingFiles[index]}
                                            />
                                            <label
                                              htmlFor={`file-upload-${index}`}
                                              className={`flex-1 cursor-pointer px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg text-center text-sm font-medium transition-colors hover:border-blue-400 hover:bg-blue-50 ${
                                                uploadingFiles[index]
                                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                                  : 'bg-white text-gray-700'
                                              }`}
                                            >
                                              {uploadingFiles[index] ? (
                                                <div className="flex flex-col items-center gap-2">
                                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                                  <span>Enviando arquivo...</span>
                                                </div>
                                              ) : (
                                                <div className="flex flex-col items-center gap-2">
                                                  <div className="text-2xl">📁</div>
                                                  <span>Clique para fazer upload do arquivo</span>
                                                  <span className="text-xs text-gray-500">
                                                    {item.type === 'image' && 'Imagens: JPG, PNG, GIF, WebP'}
                                                    {item.type === 'video' && 'Vídeos: MP4, AVI, MOV, WMV, MKV'}
                                                    {item.type === 'audio' && 'Áudios: MP3, WAV, OGG, AAC, M4A'}
                                                    {item.type === 'document' && 'Documentos: PDF, DOC, XLS, TXT, ZIP'}
                                                  </span>
                                                </div>
                                              )}
                                            </label>
                                          </div>
                                        ) : (
                                          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center gap-3">
                                                {item.type === 'image' && (
                                                  <img
                                                    src={item.content.url}
                                                    alt="Preview"
                                                    className="w-12 h-12 object-cover rounded"
                                                  />
                                                )}
                                                {item.type !== 'image' && (
                                                  <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center text-2xl">
                                                    {getFileIcon(fileInfos[index]?.type || item.type)}
                                                  </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-sm font-medium text-gray-900 truncate">
                                                    {fileInfos[index]?.name || 'Arquivo carregado'}
                                                  </div>
                                                  <div className="text-xs text-gray-500">
                                                    {fileInfos[index]?.size ? formatFileSize(fileInfos[index].size) : ''}
                                                  </div>
                                                </div>
                                              </div>
                                              <button
                                                type="button"
                                                onClick={() => handleRemoveFile(index)}
                                                className="text-red-600 hover:text-red-800 p-1"
                                                title="Remover arquivo"
                                              >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    )}

                                    {['image', 'video'].includes(item.type) && !item.content.useMediaVariations && (
                                      <div className="space-y-2">
                                        <input
                                          type="text"
                                          placeholder="Legenda (opcional) - Use variáveis como {{nome}}, {{telefone}}, {{bairro}}, {{cidade}}, etc."
                                          value={item.content.caption || ''}
                                          onChange={(e) => {
                                            const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                            const newSequence = currentSequence.map((seqItem, i) =>
                                              i === index ? {
                                                ...seqItem,
                                                content: { ...seqItem.content, caption: e.target.value }
                                              } : seqItem
                                            );
                                            setFormData(prev => ({
                                              ...prev,
                                              messageContent: { sequence: newSequence }
                                            }));
                                          }}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <div className="flex flex-wrap gap-1">
                                          <span className="text-xs text-gray-500">Variáveis:</span>
                                          {['{{nome}}', '{{telefone}}', '{{bairro}}', '{{cidade}}', '{{categoria}}'].map((variable) => (
                                            <button
                                              key={variable}
                                              type="button"
                                              onClick={() => {
                                                const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                                const currentCaption = currentSequence[index]?.content?.caption || '';
                                                const newSequence = currentSequence.map((seqItem, i) =>
                                                  i === index ? {
                                                    ...seqItem,
                                                    content: { ...seqItem.content, caption: currentCaption + variable }
                                                  } : seqItem
                                                );
                                                setFormData(prev => ({
                                                  ...prev,
                                                  messageContent: { sequence: newSequence }
                                                }));
                                              }}
                                              className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded hover:bg-blue-200"
                                            >
                                              {variable}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {item.type === 'document' && !item.content.useMediaVariations && (
                                      <input
                                        type="text"
                                        placeholder="Nome do arquivo (opcional)"
                                        value={item.content.fileName || ''}
                                        onChange={(e) => {
                                          const currentSequence = ('sequence' in formData.messageContent) ? formData.messageContent.sequence : [];
                                          const newSequence = currentSequence.map((seqItem, i) =>
                                            i === index ? {
                                              ...seqItem,
                                              content: { ...seqItem.content, fileName: e.target.value }
                                            } : seqItem
                                          );
                                          setFormData(prev => ({
                                            ...prev,
                                            messageContent: { sequence: newSequence }
                                          }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}

                          {(!('sequence' in formData.messageContent) || formData.messageContent.sequence.length === 0) && (
                            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                              <div className="text-4xl mb-4">📝</div>
                              <p className="text-lg font-medium">Nenhuma mensagem na sequência</p>
                              <p className="text-sm">Clique em "Adicionar Mensagem" para começar a criar sua campanha.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-8 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setCreateStep('channel');
                    }}
                    className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={creatingCampaign}
                    className={`w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg transition-all font-medium shadow-lg ${
                      creatingCampaign ? 'opacity-70 cursor-not-allowed' : 'hover:from-blue-700 hover:to-purple-700'
                    }`}
                  >
                    {creatingCampaign ? 'Criando...' : 'Criar Campanha'}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        )}

        {/* Modal de Relatórios */}
        {showReportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-30 p-4 sm:p-6 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[calc(100vh-2rem)] overflow-y-auto relative"
                 style={{ zIndex: 1000 }}>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4 sm:p-6 border-b">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <h3 className="text-lg font-medium text-gray-900">Relatório da Campanha</h3>
                  <button
                    onClick={handleRefreshReport}
                    disabled={reportLoading}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Atualizar relatório"
                  >
                    <svg className={`w-4 h-4 ${reportLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {reportLoading ? 'Atualizando...' : 'Atualizar'}
                  </button>
                  {reportLoading && reportData && (
                    <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                      <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse"></span>
                      Atualizando dados...
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-4 sm:p-6">
                {reportLoading && !reportData ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Carregando relatório...</span>
                  </div>
                ) : reportData ? (
                  <div className="space-y-6">
                    {/* Informações da Campanha */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">{reportData.campaign.nome}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Status:</span>
                          <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reportData.campaign.status)}`}>
                            {getStatusText(reportData.campaign.status)}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Criado por:</span>
                          <span className="ml-2 text-gray-900">
                            {reportData.campaign.createdByName || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Data de Criação:</span>
                          <span className="ml-2 text-gray-900">{new Date(reportData.campaign.criadoEm).toLocaleString('pt-BR')}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Data de Início:</span>
                          <span className="ml-2 text-gray-900">
                            {reportData.campaign.startedAt ? new Date(reportData.campaign.startedAt).toLocaleString('pt-BR') : 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Data de Conclusão:</span>
                          <span className="ml-2 text-gray-900">
                            {reportData.campaign.completedAt ? new Date(reportData.campaign.completedAt).toLocaleString('pt-BR') : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Acompanhamento de Envio */}
                    <div className="bg-white border rounded-lg p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <h4 className="text-sm font-semibold text-gray-900">Acompanhamento de envio</h4>
                        <span className="text-xs text-gray-500">
                          {reportData.campaign.status === 'RUNNING' || reportData.campaign.status === 'PENDING'
                            ? 'Atualizando a cada 5s'
                            : 'Atualização manual disponível'}
                        </span>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                        <span className="font-medium text-blue-700">Enviadas: {reportSent}</span>
                        <span className="font-medium text-gray-700">Restantes: {reportPending}</span>
                        <span className="font-medium text-red-600">Falhas: {reportFailed}</span>
                      </div>

                      <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 transition-all duration-300"
                          style={{ width: `${reportProgress}%` }}
                        />
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        {reportSent + reportFailed} de {reportTotal} processados
                      </div>
                    </div>

                    {reportPendingMessages.length > 0 && (
                      <div className="bg-white border rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900">Em envio agora</h4>
                        <ul className="mt-2 space-y-2 text-xs text-gray-600 max-h-32 overflow-y-auto">
                          {reportPendingMessages.slice(0, 6).map((message: any) => (
                            <li key={message.id} className="flex items-center justify-between gap-3">
                              <span className="truncate">{message.contactName}</span>
                              <span className="text-[10px] text-gray-400">
                                {message.sessionName || 'N/A'}
                              </span>
                            </li>
                          ))}
                        </ul>
                        {reportPendingMessages.length > 6 && (
                          <div className="mt-2 text-[11px] text-gray-500">
                            +{reportPendingMessages.length - 6} pendentes
                          </div>
                        )}
                      </div>
                    )}

                    {/* Estatísticas */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Estatísticas de Envio</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-blue-600">{reportData.stats.total}</div>
                          <div className="text-sm text-blue-800">Total de Contatos</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-green-600">{reportData.stats.sent}</div>
                          <div className="text-sm text-green-800">Mensagens Enviadas</div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-red-600">{reportData.stats.failed}</div>
                          <div className="text-sm text-red-800">Mensagens Falharam</div>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-yellow-600">{reportData.stats.pending}</div>
                          <div className="text-sm text-yellow-800">Mensagens Pendentes</div>
                        </div>
                      </div>
                    </div>

                    {/* Mensagens por Sessão */}
                    {Object.keys(reportData.messagesBySession).length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Distribuição por Sessão</h4>
                        <div className="bg-white border rounded-lg overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sessão</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enviadas</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Falharam</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {Object.entries(reportData.messagesBySession).map(([sessionName, sessionData]) => {
                                const messages = (sessionData as any).messages || [];
                                return (
                                  <tr key={sessionName}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sessionName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{messages.length}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                      {messages.filter((m: any) => m.status === 'SENT').length}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                                      {messages.filter((m: any) => m.status === 'FAILED').length}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Mensagens Detalhadas */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-lg font-semibold text-gray-900">Detalhes das Mensagens</h4>
                        <div className="flex items-center gap-2">
                          {reportInvalidContactsCount > 0 && (
                            <button
                              onClick={handleDeleteInvalidReportContacts}
                              className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
                              title="Excluir contatos com número ou e-mail inválido do arquivo do usuário"
                            >
                              Excluir inválidos ({reportInvalidContactsCount})
                            </button>
                          )}
                        <button
                          onClick={handleDownloadReport}
                          className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                        >
                          📊 Download CSV
                        </button>
                        </div>
                      </div>

                      {(() => {
                        const messages = reportData.campaign.messages || [];
                        const totalPages = Math.ceil(messages.length / reportItemsPerPage);
                        const startIndex = (reportCurrentPage - 1) * reportItemsPerPage;
                        const endIndex = startIndex + reportItemsPerPage;
                        const currentMessages = messages.slice(startIndex, endIndex);

                        return (
                          <>
                            <div className="bg-white border rounded-lg overflow-hidden overflow-visible">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sessão</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data de Envio</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Erro</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {currentMessages.map((message: any) => (
                                    <tr key={message.id}>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.contactName}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.contactPhone}</td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          message.status === 'SENT' ? 'bg-green-100 text-green-800' :
                                          message.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                                          'bg-yellow-100 text-yellow-800'
                                        }`}>
                                          {message.status === 'SENT' ? 'Enviada' :
                                           message.status === 'FAILED' ? 'Falhou' : 'Pendente'}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.sessionName || 'N/A'}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {message.sentAt ? new Date(message.sentAt).toLocaleString('pt-BR') : 'N/A'}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {message.errorMessage && message.errorMessage !== 'N/A' ? (
                                          (() => {
                                            const errorInfo = getErrorIcon(message.errorMessage);
                                            return errorInfo ? (
                                              <div className="group relative inline-flex items-center">
                                                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${errorInfo.bgColor} cursor-help`}>
                                                  <span className="text-lg">{errorInfo.icon}</span>
                                                </div>

                                                {/* Tooltip com posicionamento inteligente */}
                                                <div className="group-hover:block hidden absolute z-[9999]">
                                                  {/* Versão à direita */}
                                                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 w-96 max-w-sm p-4 text-sm bg-gray-900 text-white rounded-lg shadow-xl border border-gray-700 group-hover/right:block hidden">
                                                    {/* Seta esquerda */}
                                                    <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-1">
                                                      <div className="w-2 h-2 bg-gray-900 border-l border-b border-gray-700 rotate-45"></div>
                                                    </div>

                                                    {/* Conteúdo */}
                                                    <div className="space-y-3">
                                                      <div className="flex items-center gap-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${errorInfo.bgColor} ${errorInfo.color}`}>
                                                          {errorInfo.icon} {errorInfo.category}
                                                        </span>
                                                      </div>
                                                      <div className="font-semibold text-white text-sm">
                                                        Detalhes do Erro:
                                                      </div>
                                                      <div className="text-gray-200 break-all whitespace-normal text-xs leading-relaxed hyphens-auto overflow-wrap-anywhere">
                                                        {message.errorMessage}
                                                      </div>
                                                    </div>
                                                  </div>

                                                  {/* Versão à esquerda (fallback) */}
                                                  <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 w-96 max-w-sm p-4 text-sm bg-gray-900 text-white rounded-lg shadow-xl border border-gray-700">
                                                    {/* Seta direita */}
                                                    <div className="absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-1">
                                                      <div className="w-2 h-2 bg-gray-900 border-r border-t border-gray-700 rotate-45"></div>
                                                    </div>

                                                    {/* Conteúdo */}
                                                    <div className="space-y-3">
                                                      <div className="flex items-center gap-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${errorInfo.bgColor} ${errorInfo.color}`}>
                                                          {errorInfo.icon} {errorInfo.category}
                                                        </span>
                                                      </div>
                                                      <div className="font-semibold text-white text-sm">
                                                        Detalhes do Erro:
                                                      </div>
                                                      <div className="text-gray-200 break-all whitespace-normal text-xs leading-relaxed hyphens-auto overflow-wrap-anywhere">
                                                        {message.errorMessage}
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              <span className="text-sm text-red-600">
                                                {message.errorMessage}
                                              </span>
                                            );
                                          })()
                                        ) : (
                                          <span className="text-sm text-gray-400 flex items-center justify-center">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100">
                                              <span className="text-lg">✅</span>
                                            </div>
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>

                              {messages.length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                  Nenhuma mensagem encontrada
                                </div>
                              )}
                            </div>

                            {/* Paginação */}
                            {totalPages > 1 && (
                              <div className="flex items-center justify-between mt-4">
                                <div className="text-sm text-gray-700">
                                  Mostrando {startIndex + 1} a {Math.min(endIndex, messages.length)} de {messages.length} mensagens
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => setReportCurrentPage(page => Math.max(page - 1, 1))}
                                    disabled={reportCurrentPage === 1}
                                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Anterior
                                  </button>

                                  <div className="flex items-center gap-1">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                      <button
                                        key={pageNum}
                                        onClick={() => setReportCurrentPage(pageNum)}
                                        className={`px-3 py-2 text-sm rounded-md ${
                                          pageNum === reportCurrentPage
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                      >
                                        {pageNum}
                                      </button>
                                    ))}
                                  </div>

                                  <button
                                    onClick={() => setReportCurrentPage(page => Math.min(page + 1, totalPages))}
                                    disabled={reportCurrentPage === totalPages}
                                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Próximo
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Erro ao carregar relatório
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

